import { createApi } from "@reduxjs/toolkit/query/react"

import { ApiTypes, UITypes } from "types"

import { baseQueryWithAuth } from "utils/api"
import { prepareQueryParams } from "utils/prepareQueryParams"

export const performanceTestApi = createApi({
  reducerPath: "performanceTests",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["PerformanceTestsList", "PerformanceTestsHistoryList"],
  endpoints: builder => ({
    getPerformanceTestWidgetData: builder.query<ApiTypes.Res.PerformanceTestWidgetData, void>({
      query: () => ({
        url: `/perftest/widget`,
        headers: { Accept: "application/json" },
      }),
    }),
    getPerformanceTests: builder.query<ApiTypes.Res.PerformanceTests, UITypes.Params>({
      query: arg => ({
        url: `/devices`,
        params: prepareQueryParams(arg),
        headers: { Accept: "application/json" },
      }),
      providesTags: ["PerformanceTestsList"],
    }),
    getPerformanceTestsHistory: builder.query<ApiTypes.Res.PerformanceTestHistory, UITypes.Params>({
      query: arg => ({
        url: `/perftest/perftests`,
        params: prepareQueryParams(arg),
        headers: { Accept: "application/json" },
      }),
      providesTags: ["PerformanceTestsHistoryList"],
    }),
    getPerformanceTestLabels: builder.query<ApiTypes.Res.PerformanceTestLabels, UITypes.Params>({
      query: arg => ({
        url: `/perftest/labels`,
        headers: { Accept: "application/json" },
        params: {
          ...(arg.name ? { label: arg.name } : {}),
        },
      }),
    }),
    schedulePerformanceTest: builder.mutation<void, ApiTypes.Req.SchedulePerformanceTest>({
      query: data => ({
        url: `/perftest/schedule`,
        method: "POST",
        body: data,
        headers: { Accept: "application/json" },
      }),
      invalidatesTags: ["PerformanceTestsList"],
    }),
    changePerformanceTestStatus: builder.mutation<void, { id: number; status: "Passed" | null }>({
      query: ({ id, status }) => ({
        url: `/perftest/update/${id}`,
        method: "POST",
        body: { status },
        headers: { Accept: "application/json" },
      }),
      invalidatesTags: ["PerformanceTestsList", "PerformanceTestsHistoryList"],
    }),
    runDeviceCommand: builder.mutation<void, ApiTypes.Req.MonitoringDeviceCommand>({
      query: data => ({
        url: `/monitoring/setCommand`,
        method: "POST",
        body: data,
        headers: { Accept: "application/json" },
      }),
      invalidatesTags: ["PerformanceTestsList"],
    }),
    getPerformanceTestReport: builder.query<ApiTypes.Model.PerformanceTestReport, number>({
      query: id => ({
        url: `/perftest/device/${id}`,
        headers: { Accept: "application/json" },
      }),
    }),
    getPerformanceTestSnapshot: builder.query<ApiTypes.Res.PerformanceTestSnapshot, string>({
      query: sn => ({
        url: `/perftest/device/${sn}/snapshot`,
        headers: { Accept: "application/json" },
      }),
    }),
  }),
})

export const getPerformanceTestWidgetData = performanceTestApi.endpoints.getPerformanceTestWidgetData

export const getPerformanceTests = performanceTestApi.endpoints.getPerformanceTests

export const getPerformanceTestsHistory = performanceTestApi.endpoints.getPerformanceTestsHistory

export const getPerformanceTestLabels = performanceTestApi.endpoints.getPerformanceTestLabels

export const getPerformanceTestReport = performanceTestApi.endpoints.getPerformanceTestReport

export const {
  useLazyGetPerformanceTestWidgetDataQuery,
  useLazyGetPerformanceTestsQuery,
  useLazyGetPerformanceTestsHistoryQuery,
  useLazyGetPerformanceTestLabelsQuery,
  useSchedulePerformanceTestMutation,
  useRunDeviceCommandMutation,
  useLazyGetPerformanceTestReportQuery,
  useChangePerformanceTestStatusMutation,
  useLazyGetPerformanceTestSnapshotQuery,
} = performanceTestApi
